import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";

// NZ + AU + nearby islands topojson from world-atlas (cached CDN)
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const COLOR_BY_INTENSITY = {
    high:   "#4ADE80",
    medium: "#15803D",
    low:    "#064E3B",
};

const LABEL_OFFSET = {
    // Nudge overlapping or very-close labels so they never collide.
    NTL: { x: 10, y: -2 },  // Northland label right of marker (close to AKL)
    AKL: { x: -10, y: 2 },  // Auckland label slightly below-left
    CHC: { x: -8,  y: 2 },  // Christchurch below-left (close to WLG column)
    WLG: { x: 10,  y: -2 },
};

const RADIUS_BY_SCORE = (score) => 4 + (score / 100) * 10;

export default function DemandMap({ regions = [], onHover, hovered }) {
    return (
        <div className="relative w-full h-full">
            <ComposableMap
                projection="geoMercator"
                projectionConfig={{ center: [145, -32], scale: 620 }}
                width={900}
                height={520}
                style={{ width: "100%", height: "100%" }}
            >
                <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                        geographies
                            .filter((g) =>
                                ["New Zealand", "Australia"].includes(g.properties.name),
                            )
                            .map((geo) => (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    style={{
                                        default: {
                                            fill: "#0d0d0d",
                                            stroke: "rgba(34,197,94,0.35)",
                                            strokeWidth: 0.6,
                                            outline: "none",
                                        },
                                        hover:   { fill: "#111", outline: "none" },
                                        pressed: { fill: "#111", outline: "none" },
                                    }}
                                />
                            ))
                    }
                </Geographies>

                {regions.map((r) => {
                    const color = COLOR_BY_INTENSITY[r.intensity] || "#15803D";
                    const isHovered = hovered === r.region;
                    const radius = RADIUS_BY_SCORE(r.demand_score);
                    const offset = LABEL_OFFSET[r.code] || { x: 0, y: -radius - 6 };
                    const labelY = LABEL_OFFSET[r.code] ? offset.y : -radius - 6;
                    const labelX = LABEL_OFFSET[r.code] ? offset.x : 0;
                    const anchor = labelX > 0 ? "start" : labelX < 0 ? "end" : "middle";
                    return (
                        <Marker
                            key={r.region}
                            coordinates={[r.lng, r.lat]}
                            onMouseEnter={() => onHover?.(r.region)}
                            onMouseLeave={() => onHover?.(null)}
                            data-testid={`map-marker-${r.code.toLowerCase()}`}
                        >
                            {/* Pulse ring */}
                            <circle
                                r={radius + 4}
                                fill={color}
                                fillOpacity={isHovered ? 0.3 : 0.15}
                                style={{ transition: "fill-opacity 160ms ease" }}
                            />
                            <circle
                                r={radius}
                                fill={color}
                                stroke="#050505"
                                strokeWidth={1}
                                style={{
                                    filter: `drop-shadow(0 0 ${
                                        isHovered ? 12 : 6
                                    }px ${color})`,
                                    transition: "filter 160ms ease",
                                }}
                            />
                            <text
                                textAnchor={anchor}
                                x={labelX}
                                y={labelY}
                                style={{
                                    fontFamily: "JetBrains Mono, monospace",
                                    fontSize: 9,
                                    fill: isHovered ? "#FAFAFA" : "#A1A1AA",
                                    letterSpacing: "0.1em",
                                    pointerEvents: "none",
                                }}
                            >
                                {r.code}
                            </text>
                        </Marker>
                    );
                })}
            </ComposableMap>

            {/* Legend */}
            <div className="absolute bottom-2 right-2 flex items-center gap-3 hg-glass px-3 py-2 rounded-sm">
                <span className="hg-eyebrow text-[9px]">Demand</span>
                {[
                    { label: "LOW",  c: "#064E3B" },
                    { label: "MED",  c: "#15803D" },
                    { label: "HIGH", c: "#4ADE80" },
                ].map((l) => (
                    <div key={l.label} className="flex items-center gap-1.5">
                        <span
                            className="w-2 h-2 rounded-full"
                            style={{ background: l.c, boxShadow: `0 0 8px ${l.c}` }}
                        />
                        <span className="font-mono-data text-[10px] text-[#A1A1AA] tracking-widest">
                            {l.label}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
